import { Observable, ReplaySubject } from 'rxjs'

import { BackendStrategy } from '../backend-strategy'
import { BaseModel } from '../../../models'
import { ObjectHelper, ObservableUtils } from '../../../index'
import { ModelRequestStore, IKeyStrategy } from '../model-request-store'
import { ModelInclude, ModelIncludeFactory } from '..'
import { SearchCriteria } from '../search-criteria/search-criteria'
import { RequestCache } from './request-cache'

export class MemoryBackend implements BackendStrategy<BaseModel> {
  private _cache: RequestCache

  public constructor(private _originalBackend: BackendStrategy<BaseModel>,
                     private _modelRequestStore: ModelRequestStore<Observable<BaseModel[]>>,
                     private _keyStrategy: IKeyStrategy<BaseModel>,
                     private _observableUtils: ObservableUtils,
                     private _modelIncludeFactory: ModelIncludeFactory) {

    this._cache = new RequestCache(this._keyStrategy, this._modelRequestStore)
  }

  public findById(type: string, id: string, includes: string[] = []): Observable<BaseModel> {
    return this._delegateFind('findById', type, id, includes)
      .map((models) => Array.isArray(models) ? models[0] : models) as Observable<BaseModel>
  }

  public findBy(type: string, searchCriteria: { [key: string]: any }, includes: string[] = []): Observable<BaseModel[]> {
    return this._delegateFind('findBy', type, searchCriteria, includes) as Observable<BaseModel[]>
  }

  public save(model: BaseModel[] | BaseModel): Observable<BaseModel | BaseModel[]> {
    return this._delegateNonFind('save', model)
  }

  public delete(models: BaseModel[]): Observable<BaseModel[]> {
    return this._delegateNonFind('delete', models) as Observable<BaseModel[]>
  }

  protected _delegateFind(method: string, type: string, searchCriteria: string | { [key: string]: any }, includes: string[]):
    Observable<BaseModel[]> {

    const includesModel = this._modelIncludeFactory.fromIncludes(includes)
    if (!this._cache.isCacheable(method, type, searchCriteria, includesModel)) {
      return this._delegateCall(method, type, searchCriteria, includes) as Observable<BaseModel[]>
    }

    const getCachedRequest = (mthd, criteria) => {
      return this._cache.get(mthd, type, criteria, includesModel)
    }

    const createRequest = (mthd, criteria) => {
      const memoizedEventObservable = this._observableUtils.memoizedLazyObservable(
        this._createFirstFindRequest(mthd, type, criteria, includesModel)
      )
      this._cache.set(mthd, type, criteria, includesModel, memoizedEventObservable)
      return memoizedEventObservable
    }

    const existingSpecificRequest = getCachedRequest(method, searchCriteria)
    if (existingSpecificRequest) {
      return existingSpecificRequest
    }

    const existingAllRequest = getCachedRequest('findBy', {})
    if (existingAllRequest) {
      // If we're already requesting 'all', rather wait and use any cached request
      // If that it does not contain the requested data, we'll call the server
      return existingAllRequest
        .concatMap((ignored) => getCachedRequest(method, searchCriteria) || Observable.of(undefined))
        .concatMap((cachedData) => cachedData ? Observable.of(cachedData) :  createRequest(method, searchCriteria))
    }

    return createRequest(method, searchCriteria)
  }

  private _delegateNonFind(method: string, model: BaseModel | BaseModel[]): Observable<BaseModel | BaseModel[]> {
    return this._delegateCall(method, model).do(() => {
      const models = Array.isArray(model) ? model : [model]
      const unpersistedModels = models.filter((mdl) => !mdl.isPersisted())
      this._cache.invalidateForModelsByType(method, unpersistedModels)
    })
  }

  private _createFirstFindRequest(method: string, type: string, searchCriteria: string | { [key: string]: any },
                                  includesModel: ModelInclude): Observable<BaseModel[]> {

    const newRequest = this._delegateCall(method, type, searchCriteria, includesModel.toArray())
    const cacheStrategy = this._cache.getCacheStrategyByModel(type, searchCriteria, includesModel)
    const ensureArrayResult = (modelOrModels: BaseModel | BaseModel[]) => {
      return Array.isArray(modelOrModels) ? modelOrModels : [modelOrModels]
    }

    return newRequest.do((models) => cacheStrategy(models)).map(ensureArrayResult)
  }

  private _delegateCall(method: string, ...methodArgs: any[]): Observable<BaseModel | BaseModel[]> {
    return this._originalBackend[method](...methodArgs)
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/model-repository/backends/memory-backend/memory.backend.ts